'use client'

import type {
  GridContextOption,
  OptionLookup,
  RefDataByKey,
} from '@/types/grid'

type OptionEntry = {
  label: string
  value?: string | number
}

type DynamicLookupOption = {
  id?: string | number
  label: string
  value?: string | number
  options?: readonly DynamicLookupOption[]
} & Record<string, unknown>

type ProjectSectionsSource = ReadonlyArray<{
  value: string
  sections?: ReadonlyArray<Record<string, unknown>>
}>

type ProjectSection = {
  id?: string
  label?: string
  value?: string
  options?: ReadonlyArray<ProjectSection>
} & Record<string, unknown>

type GridOptionsLike = {
  propertyOptions?: Partial<Record<string, GridContextOption[]>>
  propertyDisplayOptions?: Partial<Record<string, GridContextOption[]>>
  refDataByKey?: RefDataByKey
  optionsByID?: Record<string, Record<string, OptionLookup>>
}

export const buildDynamicRefData = <T extends OptionEntry>(
  items: readonly T[] | undefined,
): Record<string, string> => {
  if (!items?.length) return {}

  const output: Record<string, string> = {}
  for (const item of items) {
    if (typeof item.value !== 'string' && typeof item.value !== 'number') {
      continue
    }
    output[String(item.value)] = item.label
  }

  return output
}

export const buildDynamicOptionsLookup = <T extends DynamicLookupOption>(
  items: readonly T[] | undefined,
): Record<string, OptionLookup> => {
  if (!items?.length) return {}

  const output: Record<string, OptionLookup> = {}
  const stack = [...items]

  while (stack.length > 0) {
    const item = stack.pop()
    if (!item) continue

    const register = (candidate: unknown) => {
      if (
        (typeof candidate !== 'string' && typeof candidate !== 'number') ||
        item.label.length === 0
      ) {
        return
      }

      output[String(candidate)] = item as unknown as OptionLookup
    }

    register(item.id)
    register(item.value)

    if (Array.isArray(item.options)) {
      for (const child of item.options) stack.push(child)
    }
  }

  return output
}

export const buildShapeOptionsFromProjects = (
  projects: ProjectSectionsSource | undefined,
): GridContextOption[] =>
  (projects ?? []).flatMap((project) =>
    (project.sections ?? []).flatMap((section) => {
      const normalizedSection = section as ProjectSection
      const sectionId = String(
        normalizedSection.id ??
          normalizedSection.value ??
          normalizedSection.label ??
          '',
      )
      const childEntries = Array.isArray(normalizedSection.options)
        ? normalizedSection.options.filter((option) => {
            const optionId = String(
              option.id ?? option.value ?? option.label ?? '',
            )
            return optionId !== sectionId
          })
        : []
      const entries = [normalizedSection, ...childEntries]

      return entries.map((entry) => {
        const normalizedEntry = entry as ProjectSection
        const { options: _nestedOptions, ...restEntry } = normalizedEntry
        const rawValue =
          normalizedEntry.value ?? normalizedEntry.id ?? normalizedEntry.label
        const value = String(rawValue ?? '')

        return {
          ...restEntry,
          id: String(normalizedEntry.id ?? value),
          label:
            typeof normalizedEntry.label === 'string'
              ? normalizedEntry.label
              : value,
          value,
          projectID: project.value,
        }
      })
    }),
  )

export const filterOptionsByProject = (
  items: readonly GridContextOption[] | undefined,
  projectID: string | undefined,
): GridContextOption[] => {
  if (!items?.length) return []
  if (!projectID) return [...items]

  return items.filter((item) => item.projectID === projectID)
}

export const withDynamicPropertyOptions = <T extends GridOptionsLike>(
  context: T,
  key: string,
  items: GridContextOption[],
): T & GridOptionsLike => ({
  ...context,
  propertyOptions: {
    ...context.propertyOptions,
    [key]: items,
  },
  propertyDisplayOptions: {
    ...context.propertyDisplayOptions,
    [key]: items,
  },
  refDataByKey: {
    ...context.refDataByKey,
    [key]: buildDynamicRefData(items),
  },
  optionsByID: {
    ...context.optionsByID,
    [key]: buildDynamicOptionsLookup(items),
  },
})
